import type { LanguageCode } from '../platform-languages';
import type { RecallGoal } from '../settings-recall-agent/types';

/**
 * The recall builder (spec 21 — **a rebuild, not a fix**).
 *
 * Both built pages were static: *Build the list*, *Add a condition*, *See all 412*
 * and *Add a step* did nothing, steps 1/3/4 opened to empty space, and **there was
 * no text input for any message and no way to insert a patient's details.** The
 * shared step-content panel, designed for exactly this editor, was never mounted.
 *
 * Three pages: Audience · Messages · Test and start.
 */

export type BuilderStepId = 'audience' | 'messages' | 'test';

/* ------------------------------------------------------------------ *
 * Fields — the part the old build was missing entirely.
 * ------------------------------------------------------------------ */

export type FieldKey =
  | 'first_name'
  | 'practice_name'
  | 'practice_phone'
  | 'booking_link'
  | 'month_due'
  | 'last_visit'
  | 'last_treatment'
  | 'provider'
  | 'years_with_practice'
  | 'unused_benefits';

export interface RecallField {
  key: FieldKey;
  label: string;
  /** Shown beside the label in the menu, so the writer sees what it becomes. */
  example: string;
  /** Some patients lack it, so a message using it needs a fallback. */
  mayBeEmpty: boolean;
  defaultFallback?: string;
  /** Which goals may use it at all. */
  goals: RecallGoal[];
  /** Smart-only fields are never available in a template. */
  smartOnly?: boolean;
  /** Needs a recall-due criterion in the audience. */
  needsDueCriterion?: boolean;
}

/**
 * Every field, with the rules that decide whether it can be offered.
 *
 * **Unavailable fields are shown greyed with why, never hidden** — hiding one
 * makes somebody hunt for a field that was quietly removed.
 */
export const RECALL_FIELDS: RecallField[] = [
  {
    key: 'first_name',
    label: 'First name',
    example: 'Marguerite',
    mayBeEmpty: true,
    defaultFallback: 'there',
    goals: ['book_appointment', 'reactivate_patient', 'increase_loyalty', 'clinical_safety'],
  },
  {
    key: 'practice_name',
    label: 'Practice name',
    example: 'Alamo Heights Dental',
    mayBeEmpty: false,
    goals: ['book_appointment', 'reactivate_patient', 'increase_loyalty', 'clinical_safety'],
  },
  {
    key: 'practice_phone',
    label: 'Practice phone',
    example: '(210) 555-0147',
    mayBeEmpty: false,
    goals: ['book_appointment', 'reactivate_patient', 'increase_loyalty', 'clinical_safety'],
  },
  {
    // A loyalty message carries no ask, so it carries no booking link either.
    key: 'booking_link',
    label: 'Booking link',
    example: 'a link just for them',
    mayBeEmpty: false,
    goals: ['book_appointment', 'reactivate_patient', 'clinical_safety'],
  },
  {
    key: 'month_due',
    label: 'Month they’re due',
    example: 'November',
    mayBeEmpty: false,
    goals: ['book_appointment', 'clinical_safety'],
    needsDueCriterion: true,
  },
  {
    key: 'last_visit',
    label: 'Last visit',
    example: '14 March',
    mayBeEmpty: true,
    defaultFallback: 'your last visit',
    goals: ['book_appointment', 'reactivate_patient', 'clinical_safety'],
  },
  {
    key: 'last_treatment',
    label: 'Last treatment',
    example: 'a filling on #19',
    mayBeEmpty: true,
    defaultFallback: 'your last visit',
    goals: ['book_appointment', 'reactivate_patient', 'clinical_safety'],
  },
  {
    key: 'provider',
    label: 'Their dentist or hygienist',
    example: 'Dr. Saito',
    mayBeEmpty: true,
    defaultFallback: 'your dentist',
    goals: ['book_appointment', 'reactivate_patient', 'increase_loyalty', 'clinical_safety'],
  },
  {
    key: 'years_with_practice',
    label: 'Years with the practice',
    example: '5',
    mayBeEmpty: false,
    goals: ['increase_loyalty'],
  },
  {
    // Never in a template: a figure ELVA has not checked against the record is
    // exactly the kind of number that should not be typed once and reused.
    key: 'unused_benefits',
    label: 'Unused benefits',
    example: '$620',
    mayBeEmpty: true,
    goals: ['book_appointment', 'reactivate_patient'],
    smartOnly: true,
  },
];

export const FIELD_BY_KEY: Record<FieldKey, RecallField> = RECALL_FIELDS.reduce(
  (acc, f) => ({ ...acc, [f.key]: f }),
  {} as Record<FieldKey, RecallField>,
);

/** Why a field cannot be offered right now, or `undefined` when it can. */
export function fieldUnavailableReason(
  field: RecallField,
  ctx: { goal: RecallGoal; mode: StepMode; hasDueCriterion: boolean; mentionBenefits: boolean },
): string | undefined {
  if (!field.goals.includes(ctx.goal)) {
    return field.key === 'years_with_practice'
      ? 'Only in loyalty recalls'
      : field.key === 'booking_link'
        ? 'Loyalty messages don’t ask for a booking'
        : 'Not available for this kind of recall';
  }
  if (field.smartOnly === true && ctx.mode === 'template') {
    return 'Only when ELVA writes the message (Smart)';
  }
  if (field.key === 'unused_benefits' && !ctx.mentionBenefits) {
    return 'Turn on “Mention expiring benefits” to use this';
  }
  if (field.needsDueCriterion === true && !ctx.hasDueCriterion) {
    return 'Only when the description names a recall that is due';
  }
  return undefined;
}

/* ------------------------------------------------------------------ *
 * Message bodies — tokens, never strings with braces in them.
 * ------------------------------------------------------------------ */

export type BuilderToken =
  | { kind: 'text'; text: string }
  | { kind: 'field'; field: FieldKey }
  /** Braces somebody typed that match no field. Rendered underlined red, and the
   * step cannot be completed while one is present. */
  | { kind: 'unknown'; raw: string };

export type StepChannel = 'text' | 'email' | 'ai_call' | 'human_call';
export type StepMode = 'template' | 'smart';

export const CHANNEL_LABEL: Record<StepChannel, string> = {
  text: 'Text',
  email: 'Email',
  ai_call: 'AI call',
  human_call: 'Human call',
};

export const CHANNEL_ICON: Record<StepChannel, string> = {
  text: 'chat',
  email: 'mail',
  ai_call: 'phone',
  human_call: 'patients',
};

export interface BuilderStep {
  id: string;
  /** Day 0 is the day a patient enters. */
  day: number;
  channel: StepChannel;
  /** Absent on a human call, which has no message. */
  mode?: StepMode;
  subject?: BuilderToken[];
  /** Template mode. */
  body?: BuilderToken[];
  /** Smart mode — *What should it say?* */
  brief?: string;
  /** Smart mode — *It must include*. */
  mustInclude?: FieldKey[];
  /** Smart mode, **required** — *Backup message*. */
  backup?: BuilderToken[];
  /** AI call only. */
  voicemail?: 'leave_and_stop' | 'leave_and_retry' | 'no_voicemail';
  /** Human call only, optional. */
  callerNote?: string;
  /** Per-field fallbacks for fields some patients lack. */
  fallbacks?: Partial<Record<FieldKey, string>>;
}

/* ------------------------------------------------------------------ *
 * The audience.
 * ------------------------------------------------------------------ */

export interface Suggestion {
  id: string;
  title: string;
  /** The description it fills in — a suggestion is a pre-written description with
   * pre-built criteria, so clicking it builds at once with no model call. */
  description: string;
  matches: number;
  /**
   * Recalls already running over these patients, by id in the recall store.
   * The *not in a recall yet* figure is computed from this rather than stored,
   * because stored it contradicted the list page (fix 23 §5.2).
   */
  coveredBy?: string[];
  /** Set when the data behind it is not available — shown disabled with the
   * reason, never hidden. */
  unavailable?: string;
}

/**
 * **The readback lines come from the API** (§3.4). Code, not the model, writes
 * them — one sentence per criterion, from a template per criterion — so *what you
 * read is what runs*, because both come from the same criteria.
 */
export interface Readback {
  understood: string[];
  /** A phrase the model could not map, and why it was refused. */
  leftOut: { phrase: string; reason: string }[];
  /** The standing exclusions, worded per goal. */
  alwaysLeftOut: string;
  /** Whether the criteria include a clinical one — clinical safety needs it. */
  hasClinicalCriterion: boolean;
  /** Whether a recall-due criterion is present, which gates `month_due`. */
  hasDueCriterion: boolean;
}

export interface AudiencePatient {
  id: string;
  name: string;
  meta: string;
  why: string;
  lastVisit: string;
  due: string;
  contact: 'will' | 'later' | 'opted_out';
  /**
   * The language this patient's messages go in (amendment 25 §7). The recall
   * builder gets the same treatment as the reminders editor, because it is the
   * same editor — one component, mounted twice.
   */
  language?: LanguageCode;
  /** Sample values, so the preview can show what each field became. */
  values: Partial<Record<FieldKey, string>>;
  /** What the patient really sees, in their language. */
  rendered?: string;
}

export const CONTACT_LABEL: Record<AudiencePatient['contact'], string> = {
  will: 'Will be contacted',
  later: 'Tried again later',
  opted_out: 'Opted out of these messages',
};

export interface AudienceCount {
  match: number;
  contactable: number;
  /** Every one of the difference accounted for. The first two stay in. */
  reasons: { label: string; n: number; staysIn: boolean }[];
}

export interface RecallDraft {
  name: string;
  goal?: RecallGoal;
  description: string;
  readback?: Readback;
  count?: AudienceCount;
  patients: AudiencePatient[];
  leftOut: string[];
  steps: BuilderStep[];
  mentionBenefits: boolean;
  sendAlone: boolean;
}

/* ------------------------------------------------------------------ *
 * What blocks a start.
 * ------------------------------------------------------------------ */

export interface StepIssue {
  stepId: string;
  stepIndex: number;
  /** Short enough for the step card's red dot line. */
  short: string;
  /** The fix-list wording. */
  long: string;
}
